import {
  Expandable,
  Slot,
  SlotListResponse,
  User,
} from '@allara-health/source-health-client'
import {
  Button,
  ChakraProvider,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react'
import { ChakraPrimaryButton } from 'components/Button'
import CancelAppointmentModal from 'components/Scheduling/CancelAppointmentModal'
import ProviderDetails from 'components/Scheduling/ProviderDetails'
import TimingDetails from 'components/Scheduling/TimingDetails'
import LoadingState from 'components/Spinner'
import { RoutePaths } from 'containers/Core/Routes'
import { useSchedulingContext } from 'context/SchedulingContext/SchedulingProvider'
import { useSourceContext } from 'context/SourceContext/SourceProvider'
import { endOfMonth, format, startOfMonth } from 'date-fns'
import { formatInTimeZone, utcToZonedTime } from 'date-fns-tz'
import withAccount, { WithAccountProps } from 'hoc/withAccount'
import useSourcePrecheck from 'hooks/useSourcePrecheck'
import useMember from 'hooks/useUser/useMember'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  AppointmentBookAvailabilityShowEventProps,
  AppointmentBookSlotSelectedEventProps,
  AppointmentBookStartEventProps,
  AppointmentModifyEventProps,
  BookingEventSource,
  ProviderId,
} from 'utils/amplitude.types'
import { Amplitude } from 'utils/amplitude.utils'
import { isWithin24Hours } from 'utils/appointments.utils'
import { useIsInsurancePatient } from 'utils/program.utils'
import Calendar, {
  Chip,
  ControlHeader,
} from '../../components/Scheduling/Calendar'

/**
 * Enhanced provider type that includes availability information
 */
export type EnhancedProvider = User & {
  availableSlots: Slot[]
}

const BookScreen: React.FC<
  WithAccountProps & {
    setDaysSinceNearestSlot: (days: number) => void
    firstAvailabilityForBookingSession: string | null
    setFirstAvailabilityForBookingSession: (date: string) => void
  }
> = ({
  user,
  setDaysSinceNearestSlot,
  firstAvailabilityForBookingSession,
  setFirstAvailabilityForBookingSession,
}) => {
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [
    hasSentShowAvailabilityEvent,
    setHasSentShowAvailabilityEvent,
  ] = useState(false)
  const [
    lastShowAvailabilityEvent,
    setLastShowAvailabilityEvent,
  ] = useState<AppointmentBookAvailabilityShowEventProps | null>(null)
  const [slots, setSlots] = useState<Slot[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [expandedProviders, setExpandedProviders] = useState<
    Record<string, Record<string, boolean>>
  >({})
  const isMobile = useBreakpointValue({ base: true, md: false })
  const { source } = useSourceContext()
  const { member } = useMember()
  const [
    calendarIsAllowedToAutoAdvance,
    setCalendarIsAllowedToAutoAdvance,
  ] = useState(true)

  const [viewState, setViewState] = useState<
    | 'loading'
    | 'preCheckResultIsFalse'
    | 'noSlots'
    | 'slotsAvailable'
    | 'pastPlanningHorizon'
  >('loading')

  const { isOpen, onClose, onOpen } = useDisclosure()
  const isInsurancePatient = useIsInsurancePatient(user)

  const history = useHistory()
  const {
    result: precheckResult,
    isLoading: isPrecheckLoading,
  } = useSourcePrecheck()

  const {
    amplitudeBookingMetadata,
    appointment,
    appointmentType,
    isReschedule,
    rebookingFlowData,
  } = useSchedulingContext()

  const latestEnd = new Date()
  latestEnd.setDate(
    latestEnd.getDate() + (appointmentType?.planning_horizon ?? 0)
  )

  const onClickDashboardButton = () => {
    history.push(RoutePaths.DASHBOARD + `?from=scheduling`)
  }

  const onClickContactConciergeButton = () => {
    if (isInsurancePatient && !user?.stripeCustomer?.product) {
      window.location.href = `mailto:concierge@allarahealth.com?subject=${encodeURIComponent(
        'Potential patient not seeing availability'
      )}`
      return
    } else {
      history.push(RoutePaths.DASHBOARD_NEW_THREAD + '?sbj=8')
    }
  }

  // Update the view state based on the loading state and the availability of slots
  useEffect(() => {
    let timeoutId: number | null = null

    if (precheckResult?.result === false) {
      // Set a timeout to show the failure state after a delay
      timeoutId = window.setTimeout(() => {
        setViewState('preCheckResultIsFalse')
        onOpen()
      }, 5000)
    } else if (isPrecheckLoading || isLoading) {
      setViewState('loading')
    } else if (noSlotsAvailableOnDay()) {
      setViewState('noSlots')
    } else if (latestEnd < selectedDate) {
      setViewState('pastPlanningHorizon')
    } else {
      setViewState('slotsAvailable')
    }

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [selectedDate, isLoading, precheckResult, isPrecheckLoading])

  const {
    currentScreen,
    timeZone,
    selectedSlot: contextSelectedSlot,
    provider,
    setSelectedSlot: setContextSelectedSlot,
    setProvider,
    setDateTime,
    nextScreen,
  } = useSchedulingContext()

  const chipState = (
    state:
      | 'active'
      | 'selected'
      | 'disabled'
      | 'disabled-with-border'
      | 'active-no-border'
  ) => {
    switch (state) {
      case 'active':
        return 'chip chip-with-border'
      case 'selected':
        return 'chip chip-picked text-white'
      case 'disabled':
        return 'chip chip-disabled'
      case 'active-no-border':
        return 'chip chip-no-border'
      case 'disabled-with-border':
        return 'chip chip-with-border chip-disabled'
      default:
        break
    }
  }

  const slotsForSelectedDate = useMemo(
    () =>
      slots?.filter((slot) => {
        const date = utcToZonedTime(slot.start_at, timeZone ?? '')
        return date.toDateString() === selectedDate.toDateString()
      }) || [],
    [slots, selectedDate, timeZone]
  )

  const daysBetweenDates = (earlierDate: Date, laterDateAsIso: string) => {
    // Create Date objects for the slot time and current time in UTC
    const slotTime = new Date(laterDateAsIso)
    const earlierTime = earlierDate

    // Set both times to midnight UTC to calculate whole days
    const slotDate = new Date(
      Date.UTC(
        slotTime.getUTCFullYear(),
        slotTime.getUTCMonth(),
        slotTime.getUTCDate()
      )
    )

    const earlier = new Date(
      Date.UTC(
        earlierTime.getUTCFullYear(),
        earlierTime.getUTCMonth(),
        earlierTime.getUTCDate()
      )
    )

    // Calculate the difference in milliseconds
    const differenceMs = slotDate.getTime() - earlier.getTime()

    // Convert to days, rounding down
    const days = Math.floor(differenceMs / (1000 * 60 * 60 * 24))

    // Return 0 if the dates are the same (today)
    return Math.max(0, days)
  }

  const providersForSelectedDate = useMemo(() => {
    if (!slotsForSelectedDate?.length) {
      return
    }

    const extractUserId = (user: Expandable<User>): string => {
      return 'id' in user ? user.id : ((user as unknown) as User).object
    }

    // Make a list of unique users for the selected date
    const usersFlatMap = slotsForSelectedDate.flatMap((slot) => slot.available)
    const uniqueUsers = Array.from(
      new Map(
        usersFlatMap.map((user) => {
          return [extractUserId(user), user]
        })
      ).values()
    ) as User[]

    // add available slots to the unique users
    return uniqueUsers.map((user) => ({
      ...user,
      availableSlots: slotsForSelectedDate.filter((slot) =>
        slot.available.some(
          (expandibleUser) => extractUserId(expandibleUser) === user.id
        )
      ),
    }))
  }, [slotsForSelectedDate, selectedDate])

  const fetchSlots = useCallback(
    async (date: Date) => {
      setIsLoading(true)
      setHasSentShowAvailabilityEvent(false)

      if (!appointmentType || !member?.id || !source) {
        setIsLoading(false)
        return
      }

      try {
        const startDate = startOfMonth(date)

        const response: SlotListResponse = await source.scheduling.slots.list(
          {
            start_at: startDate.toISOString(),
            end_at: endOfMonth(date).toISOString(),
            appointment_type: appointmentType.id ?? '',
            member: member?.id,
          },
          {
            expand: ['slots.available'],
          }
        )

        setSlots(response.slots)

        // Update the selected date to the earliest slot
        if (response.slots.length) {
          const earliestSlot = response.slots.reduce((earliest, current) =>
            new Date(current.start_at) < new Date(earliest.start_at)
              ? current
              : earliest
          )

          if (earliestSlot) {
            setSelectedDate(new Date(earliestSlot.start_at))
          }
        }
      } catch (error) {
        console.error('Error fetching slots:', error)
      } finally {
        setIsLoading(false)
      }
    },
    [source, appointmentType, member?.id]
  )

  const firePostCancellationAmplitudeAppointmentBookEvents = useCallback(() => {
    if (!lastShowAvailabilityEvent) {
      return
    }

    const amplitudeBookStartEventProps: AppointmentBookStartEventProps = {
      appointment_id: null,
      is_reschedule: false,
      appointment_type_name: appointmentType?.name ?? '',
      source: BookingEventSource.APPOINTMENT_CANCEL_DONE,
      patient_action_id: null,
      patient_action_definition: null,
    }
    Amplitude.appointmentBookStart(amplitudeBookStartEventProps)
    Amplitude.appointmentBookAvailabilityShow({
      ...lastShowAvailabilityEvent,
      source: BookingEventSource.APPOINTMENT_CANCEL_DONE,
      patient_action_id: null,
      patient_action_definition: null,
    })
  }, [lastShowAvailabilityEvent, amplitudeBookingMetadata])

  useEffect(() => {
    if (hasSentShowAvailabilityEvent || isLoading) {
      return
    }

    const nearestSlotForSelectedDate =
      [...slotsForSelectedDate].sort(
        (a, b) =>
          new Date(a.start_at).getTime() - new Date(b.start_at).getTime()
      )[0] ?? undefined

    let isFirstAvailability = false

    if (
      firstAvailabilityForBookingSession === null &&
      nearestSlotForSelectedDate
    ) {
      setFirstAvailabilityForBookingSession(
        nearestSlotForSelectedDate?.start_at
      )
      isFirstAvailability = true
    }

    const availableProvidersAndSlots = providersForSelectedDate?.reduce(
      (acc, provider) => ({
        ...acc,
        [provider.id as ProviderId]: provider.availableSlots.map(
          (slot) => slot.start_at
        ),
      }),
      {} as Record<ProviderId, string[]>
    )

    setHasSentShowAvailabilityEvent(true)
    const amplitudeEventProps: AppointmentBookAvailabilityShowEventProps = {
      appointment_id: appointment?.id ?? '',
      appointment_type_name: appointmentType?.name ?? '',
      is_reschedule: isReschedule ?? false,
      slots_date: selectedDate.toISOString().split('T')[0],
      nearest_slot_time: nearestSlotForSelectedDate?.start_at ?? '',
      available_slots_cnt: slotsForSelectedDate?.length ?? 0,
      available_slot_times:
        slotsForSelectedDate?.map((slot) => slot.start_at) ?? [],
      available_providers_cnt: providersForSelectedDate?.length ?? 0,
      available_providers:
        providersForSelectedDate?.map(
          (provider) => provider.id as ProviderId
        ) ?? [],
      available_providers_and_slots: JSON.stringify(availableProvidersAndSlots),
      current_month_available_slots_cnt: slots?.length ?? 0,
      current_month_available_slot_dates_cnt: datesFromSlots.length,
      state: user.waitlistUser.state.code,
      carrier: user.waitlistUser.insuranceProvider ?? '',
      is_first_availability: isFirstAvailability,
      days_to_nearest_slot: daysBetweenDates(
        new Date(),
        nearestSlotForSelectedDate?.start_at ?? ''
      ),
      ...amplitudeBookingMetadata,
    }
    setLastShowAvailabilityEvent(amplitudeEventProps)
    Amplitude.appointmentBookAvailabilityShow(amplitudeEventProps)
  }, [hasSentShowAvailabilityEvent, providersForSelectedDate, isLoading])

  /* Fetch slots for the current month when the component mounts */
  useEffect(() => {
    if (appointmentType && member?.id && source) {
      fetchSlots(selectedDate)
    }
  }, [member?.id, appointmentType?.id, source])

  const noSlotsAvailableOnDay = (): boolean => {
    if (selectedDate < latestEnd) {
      const todaySlotsLength = slotsForSelectedDate.length
      return todaySlotsLength === 0
    }

    return false
  }

  const pickDate = (date: Date) => {
    setSelectedDate(date)
    setHasSentShowAvailabilityEvent(false)

    if (date.getMonth() !== selectedDate.getMonth()) {
      fetchSlots(date)
    }
  }

  const changeCalendar = (month: 1 | -1) => {
    const newMonth = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth() + month,
      1
    )
    pickDate(newMonth)
  }

  useEffect(() => {
    // When component is mounted, if there are not slots available, set the selected date to the first day of the next month
    if (
      calendarIsAllowedToAutoAdvance &&
      !isLoading &&
      slots.length === 0 &&
      selectedDate.getMonth() === new Date().getMonth()
    ) {
      changeCalendar(1)
      setCalendarIsAllowedToAutoAdvance(false)
    }
  }, [slots, isLoading])

  const pickSlot = (slot: Slot, provider: EnhancedProvider) => {
    setProvider(provider)
    setDateTime(new Date(slot.start_at))
    setContextSelectedSlot(slot)
    const daysSinceNearestSlot = daysBetweenDates(
      new Date(firstAvailabilityForBookingSession ?? ''),
      slot.start_at
    )
    setDaysSinceNearestSlot(daysSinceNearestSlot)
    const amplitudeEventProps: AppointmentBookSlotSelectedEventProps = {
      appointment_id: appointment?.id ?? '',
      appointment_type_name: appointmentType?.name ?? '',
      is_reschedule: isReschedule ?? false,
      slot_time: slot.start_at,
      provider_id: provider.id,
      days_since_nearest_slot: daysSinceNearestSlot,
      state: user.waitlistUser.state.code,
      carrier: user.waitlistUser.insuranceProvider ?? '',
      ...amplitudeBookingMetadata,
    }
    Amplitude.appointmentBookSlotSelected(amplitudeEventProps)
    // Navigate to the next screen
    nextScreen()
  }

  const datesFromSlots: string[] = useMemo(() => {
    // Gets unique dates from slots
    const dates = Array.from(
      new Set(
        slots?.map((slot) =>
          utcToZonedTime(slot.start_at, timeZone ?? '').toDateString()
        )
      )
    )
    return dates
  }, [slots])

  const [
    isInitialCancelAppointmentModalOpen,
    setIsInitialCancelAppointmentModalOpen,
  ] = useState<boolean>(false)

  const amplitudeEventProps: AppointmentModifyEventProps = {
    appointment_id: appointment?.id ?? '',
    appointment_type_name: appointmentType?.name ?? '',
    appointment_time: appointment?.start_at ?? '',
  }

  const onOpenCancelAppointmentModal = () => {
    Amplitude.appointmentCancelStart(amplitudeEventProps)
    setIsInitialCancelAppointmentModalOpen(true)
  }

  const cancellationModalContent = {
    listHeader: 'Each appointment can help you:',
    listItems: [
      'Get closer to reaching your goals',
      'Monitor your progress',
      'Address any new symptoms or concerns',
    ],
  }

  useEffect(() => {
    // Automatically set provider and slot if any exist in the context
    setContextSelectedSlot(contextSelectedSlot)
    setProvider(provider)
    setSelectedDate(
      utcToZonedTime(
        contextSelectedSlot?.start_at ?? new Date(),
        timeZone ?? ''
      )
    )
  }, [currentScreen])

  // Function to render provider's time slots in rows of 3
  const renderProviderTimeSlots = (provider: EnhancedProvider) => {
    const handleExpandClick = (hasToExpand: boolean) => {
      setExpandedProviders((prev) => {
        const newState = { ...prev }

        if (!newState[dateKey]) {
          newState[dateKey] = {}
        }

        newState[dateKey][provider.id] = hasToExpand

        return newState
      })
    }

    // Get current date key
    const dateKey = timeZone
      ? format(utcToZonedTime(selectedDate, timeZone), 'yyyy-MM-dd')
      : format(selectedDate, 'yyyy-MM-dd')

    // Check if this provider is expanded for the current date
    const isExpanded = expandedProviders[dateKey]?.[provider.id] || false

    // Sort slots by time
    const sortedSlots = [...provider.availableSlots].sort(
      (a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime()
    )

    // Determine how many rows to display based on expanded state
    const slotsByRow = 3
    const totalRows = Math.ceil(sortedSlots.length / slotsByRow)
    const defaultRows =
      providersForSelectedDate && providersForSelectedDate?.length > 2 ? 2 : 4
    const visibleRows = isExpanded
      ? totalRows
      : Math.min(defaultRows, totalRows)
    const hasMoreSlots = sortedSlots.length > defaultRows * slotsByRow

    return (
      <div className="provider-slots-container mt-2">
        {Array.from({ length: visibleRows }).map((_, rowIndex) => (
          <div key={`row-${rowIndex}`} className="d-flex mb-2">
            {Array.from({ length: slotsByRow }).map((_, colIndex) => {
              const slotIndex = rowIndex * slotsByRow + colIndex
              const slot =
                slotIndex < sortedSlots.length
                  ? sortedSlots[slotIndex]
                  : undefined

              return (
                <div
                  key={`slot-${rowIndex}-${colIndex}`}
                  className="flex-1 mx-1"
                  style={{ flex: '1 0 30%' }}
                >
                  {slot && (
                    <Chip
                      onClick={() => pickSlot(slot, provider)}
                      key={slot.start_at}
                      text={formatInTimeZone(
                        new Date(slot.start_at),
                        timeZone ?? '',
                        'h:mm aaa'
                      )}
                      state={`${chipState(
                        'active'
                      )} chip-slot w-100 time-slot-chip`}
                    />
                  )}
                </div>
              )
            })}
          </div>
        ))}

        {hasMoreSlots && (
          <div className="d-flex justify-content-center mt-3">
            <div
              className="cursor-pointer ff-inter-medium link see-more-availability"
              onClick={() => handleExpandClick(!isExpanded)}
            >
              {isExpanded ? 'Show less' : 'See more availability'}
            </div>
          </div>
        )}
      </div>
    )
  }

  const isAppointmentModifiable = useMemo(
    () => (appointment ? !isWithin24Hours(appointment.start_at) : false),
    [appointment]
  )

  const showCancelButton = useMemo(() => {
    return isAppointmentModifiable && isReschedule && rebookingFlowData === null
  }, [isAppointmentModifiable, isReschedule, rebookingFlowData])

  return (
    <ChakraProvider>
      <div className="screen-container">
        {rebookingFlowData && (
          <>
            <h1
              style={{
                fontFamily: 'Instrument Serif',
                fontWeight: 400,
                fontSize: '32px',
              }}
            >
              Your appointment has been canceled
            </h1>
            <Flex mb={8}>
              <ProviderDetails provider={rebookingFlowData.provider} />
              <TimingDetails
                date={rebookingFlowData.dateTime}
                timeZone={rebookingFlowData.timeZone}
                appointmentType={rebookingFlowData.appointmentType}
              />
            </Flex>
          </>
        )}
        <div className="appointment d-flex flex-column">
          <h1
            style={{
              fontFamily: 'Instrument Serif',
              fontWeight: 400,
              fontSize: '32px',
            }}
          >
            {rebookingFlowData
              ? 'Rebook your appointment'
              : isReschedule
              ? 'Reschedule your appointment'
              : 'Schedule your appointment'}
          </h1>
          <p
            style={{
              fontWeight: 500,
              fontSize: '16px',
            }}
          >
            {isReschedule || rebookingFlowData
              ? `Let's find a time that works ${
                  rebookingFlowData ? 'best' : 'better'
                } for you`
              : 'Please select a date and time for your appointment'}
          </p>
          {isMobile && showCancelButton && (
            <div className="d-flex align-items-start appointment-sub">
              <Button
                onClick={onOpenCancelAppointmentModal}
                marginTop="10px"
                marginBottom="10px"
                // on mobile only
                textDecoration="underline"
                textUnderlineOffset="3px"
                variant="link"
                color="var(--blue-dark)"
                fontWeight={600}
                fontSize="16px"
                _hover={{ textDecoration: 'underline' }}
              >
                Need to cancel instead?
              </Button>
            </div>
          )}
          <div className="d-flex align-items-start appointment-sub">
            <div>
              <ControlHeader
                previous={() => {
                  changeCalendar(-1)
                }}
                next={() => {
                  changeCalendar(1)
                }}
                title={`${format(selectedDate, 'EEEE')}, ${format(
                  selectedDate,
                  'MMMM'
                )} ${selectedDate.getDate()}`}
              />
              <Calendar
                datesFromSlot={datesFromSlots}
                date={selectedDate}
                setDate={pickDate}
                slots={datesFromSlots}
              />
              {!isMobile && showCancelButton && (
                <Button
                  onClick={onOpenCancelAppointmentModal}
                  marginTop="40px"
                  marginBottom="10px"
                  textUnderlineOffset="3px"
                  variant="link"
                  color="var(--blue-dark)"
                  fontWeight={600}
                  fontSize="16px"
                  _hover={{ textDecoration: 'underline' }}
                >
                  Cancel appointment
                </Button>
              )}
            </div>
            <div className="appointment-container-scheduling ml-md-5 ">
              {(() => {
                switch (viewState) {
                  case 'loading':
                    return <LoadingState />
                  case 'preCheckResultIsFalse':
                    return (
                      <>
                        <Modal isOpen={isOpen} onClose={onClose} isCentered>
                          <ModalOverlay />
                          <ModalContent>
                            <ModalHeader>
                              Why don&apos;t I see any available appointments?
                            </ModalHeader>
                            <ModalCloseButton />
                            <ModalBody>
                              <Text>
                                To ensure that you don&apos;t incur out of
                                network charges, our insurance team needs to
                                confirm a few details of your plan and match you
                                with participating providers. This may take up
                                to one business day.
                              </Text>
                              <Text>
                                In the meantime, if you have any questions
                                please reach out to our Patient Concierge Team.
                              </Text>
                            </ModalBody>

                            <ModalFooter justifyContent="center" gap="1rem">
                              <ChakraPrimaryButton
                                onClick={onClickContactConciergeButton}
                              >
                                Contact Concierge
                              </ChakraPrimaryButton>
                              {!isInsurancePatient && (
                                <ChakraPrimaryButton
                                  onClick={onClickDashboardButton}
                                >
                                  Go to Dashboard
                                </ChakraPrimaryButton>
                              )}
                            </ModalFooter>
                          </ModalContent>
                        </Modal>
                      </>
                    )
                  case 'noSlots':
                    return (
                      <Stack justifyContent="center" mt={2} display="flex">
                        <Text fontSize="sm">No appointments available.</Text>
                        <Text fontSize="sm">
                          Please select another date to view available
                          appointments.
                        </Text>
                      </Stack>
                    )

                  case 'slotsAvailable':
                    return (
                      <>
                        <div className="providers-list-container">
                          {providersForSelectedDate?.length ?? 0 > 0 ? (
                            <div className="d-flex flex-column">
                              {providersForSelectedDate?.map((provider) => (
                                <div
                                  key={provider.id}
                                  className="provider-container mb-4"
                                >
                                  <ProviderDetails
                                    provider={provider}
                                    className="provider-details-book-screen"
                                  />
                                  {renderProviderTimeSlots(provider)}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <Stack
                              justifyContent="center"
                              display="flex"
                              mt={2}
                              spacing={0}
                            >
                              <Text fontSize="sm">
                                No providers available for this date.
                              </Text>
                              <Text fontSize="sm">
                                Please select another date to view available
                                appointments.
                              </Text>
                            </Stack>
                          )}
                        </div>
                      </>
                    )
                  case 'pastPlanningHorizon':
                    return (
                      <Stack
                        justifyContent="center"
                        display="flex"
                        mt={2}
                        spacing={0}
                      >
                        <Text fontSize="sm" fontWeight={600}>
                          Looking for a time that works?
                        </Text>
                        <Text fontSize="sm">
                          You&apos;ve reached the end of the booking window, but
                          we&apos;re here to help. Message our{' '}
                          <Link
                            href={RoutePaths.DASHBOARD_NEW_THREAD + '?sbj=8'}
                            color={'var(--blue-light)'}
                          >
                            Concierge team
                          </Link>
                          , or give us a call at 
                          <Link
                            href={'tel:(201) 564-5109'}
                            color={'var(--blue-light)'}
                          >
                            (201) 564-5109
                          </Link>
                          . We Team can help get you scheduled at a time that
                          works for you.
                        </Text>
                      </Stack>
                    )
                  default:
                    return <LoadingState />
                }
              })()}
            </div>
          </div>
        </div>
        <CancelAppointmentModal
          open={isInitialCancelAppointmentModalOpen}
          handleClose={() => setIsInitialCancelAppointmentModalOpen(false)}
          headerText="Are you sure you want to cancel?"
          content={cancellationModalContent}
          onCancel={firePostCancellationAmplitudeAppointmentBookEvents}
        />
      </div>
    </ChakraProvider>
  )
}

export default memo(withAccount(BookScreen))
